import { startTransition, useContext, useEffect, useMemo, useRef } from "react";
import { UAParser } from "ua-parser-js";

import { AvTabState } from "../shared/types/av-state";
import { MonitoringEventTypeType } from "../shared/types/monitoring";
import { useBreakoutInSessionObserver } from "../utils/hooks/use-breakout-in-session-observer";

import { PubsubWebsocketContext } from "./PubsubWebsocketProvider";

import { serverLogout } from "api/api-server-actions";
import { useAIContext } from "components/ai/hooks/use-ai-context";
import {
  ASSESSMENT_VIEW_QUERY_PARAM,
  FOLLOWING_QUERY_PARAM,
  SECONDARY_OVERLAY_QUERY_PARAM,
  SecondaryOverlay,
  SecondaryOverlayType,
  TAB_QUERY_PARAM,
} from "components/constants";
import { useHomeRole } from "components/materials/home/use-home-role";
import { confirmProjectorViewOpen } from "components/materials/presentation/projector/projector-messaging";
import { ClassSessionCategory } from "components/server-types";
import { t } from "i18n/i18n";
import { useOnChanged } from "mds/hooks/use-on-changed";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useCapabilities } from "store/capability-hooks";
import { store, storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  pageSelectors,
  selectActiveClassSession,
  selectApiError,
  selectCurrentClassUser,
  selectCurrentOrgRole,
  selectCurrentPageId,
  selectCurrentSectionId,
  selectCurrentUserId,
  selectCurrentUserIsMPAdmin,
  selectIsClassSessionVirtual,
  selectIsRecordingAgent,
  selectIsSectionVersionOutOfSync,
  userSelectors,
} from "store/selectors";
import {
  selectAvTabState,
  selectClassStateLastUpdatedAt,
  selectIsMainAvTab,
  selectIsObservingVirtualClass,
} from "store/selectors/pubsub-virtual-class-state";
import { selectClassUsersWithIsVisibleInClassCapability } from "store/selectors/virtual-classes";
import { authStateActions } from "store/slices/auth";
import { localStateActions } from "store/slices/local";
import { PubsubVirtualClassStateActions } from "store/slices/pubsub-virtual-class-state";
import { useListSelector } from "store/store-hooks";
import { toastLocalizedOperationError, toastWarningMessage } from "utils/alerts";
import { maybeInitAmplitude, trackUserProperty } from "utils/amplitude";
import { playBreakoutAudio, playHandRaiseAudio, stopTickingAudio } from "utils/audio";
import { getOrgIdFromCookie } from "utils/auth";
import { AV_SUPPORTED_BROWSERS, useAv, useAvPubsubMessaging } from "utils/av";
import { useBreakoutEndObserver } from "utils/hooks/use-breakout-end-observer";
import { useBreakoutEndingSoonObserver } from "utils/hooks/use-breakout-ending-soon-observer";
import { useConnectionQualityObserver } from "utils/hooks/use-connection-quality-observer";
import { useCreateMonitoringEvent } from "utils/hooks/use-create-monitoring-event";
import { useHomeUrl } from "utils/hooks/use-home-url";
import { useParticipantBarObserver } from "utils/hooks/use-participant-bar-observer";
import { useTimerEndObserver } from "utils/hooks/use-timer-end-observer";
import { useUnifiedAutoMute } from "utils/hooks/use-unified-auto-mute";
import { logInfoWithDebugFlag, rollbarAndLogError } from "utils/logger";
import { getNavigationTimingMetrics } from "utils/monitoring";
import { usePresentation } from "utils/presentation";
import { useLocation, useNavigate, useQueryParamsManager } from "utils/routing";
import { useNavigationObserver } from "utils/routing/hooks/use-navigation-observer";
import {
  CONTENT_SIDEBAR_TAB_NAME,
  PROJECTOR_PATH_REGEX,
  SectionPageCategoryToSidebarTab,
  pageUrl,
  shouldAddOverlayParam,
  useViewLevel,
} from "utils/urls";
import { isAVEnabled, isAvIgnoreBrowserChecksEnabled } from "utils/waffle-flags";

export const Observers = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { deleteQueryParams, getQueryParam } = useQueryParamsManager();

  const userId = useAppSelector(selectCurrentUserId);
  const user = useAppSelector((s) => userSelectors.selectById(s, userId));
  const { canAuthorSection, canAuthorOrg } = useCapabilities();
  const isMPAdmin = useAppSelector(selectCurrentUserIsMPAdmin);
  const sectionId = useAppSelector(selectCurrentSectionId);
  const classSession = useAppSelector(selectActiveClassSession);
  const isSectionVersionOutOfSync = useAppSelector(selectIsSectionVersionOutOfSync);
  const preVersionPlaybackPageId = useRef<string | null>(null);
  const isSmallScreen = !useIsMdOrLarger();
  const viewLevel = useViewLevel();
  const { isInVirtualClass } = useAv();

  const selectedPageId = useAppSelector(selectCurrentPageId);

  const { isFollowing, isPresenting, hasProjectorView } = usePresentation();
  const hasFollowingParam = getQueryParam(FOLLOWING_QUERY_PARAM) === "1";

  const isProjectorPath = PROJECTOR_PATH_REGEX.test(location.pathname);
  const showingProjectorView = isPresenting && hasProjectorView;

  const email = user?.email;

  useConnectionQualityObserver();

  useNavigationObserver();

  // Get UserAgent info
  useEffect(() => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return;
    }

    const parser = new UAParser(window.navigator.userAgent);
    const result = parser.getResult();
    const browserName = result.browser.name || "";
    const browserVersion = result.browser.version || "";
    const os = result.os.name || "";
    const osVersion = result.os.version || "";

    dispatch(
      localStateActions.setUserAgentInfo({
        isBrowserAvCompatible:
          isAvIgnoreBrowserChecksEnabled() || AV_SUPPORTED_BROWSERS.includes(browserName),
        browserName,
        browserVersion,
        os,
        osVersion,
      }),
    );
  }, [dispatch]);

  useBreakoutEndObserver({
    breakoutSessionEndsAt: classSession?.breakout_session_ends_at,
  });

  useTimerEndObserver({
    timerEndsAt: classSession?.timer_ends_at,
    classSessionId: classSession?.id,
    canAuthorSection: !!canAuthorSection,
  });

  useBreakoutInSessionObserver({ isInVirtualClass });

  useUnifiedAutoMute({ isInVirtualClass });

  useEffect(() => {
    // Load data that can be used on all pages
    if (userId) {
      storeApi.users.list();
      storeApi.orgs.list();
      storeApi.org_users.list();
    }
  }, [userId]);

  // Navigation logic for /home route and root path
  // Redirects both "/" and "/home" directly to "/home/<user-role>" to avoid double redirects.
  // Adding this here as it ensures child components have rendered before navigation
  // which we hope avoids the "Failed to execute 'insertBefore' on 'Node'" error
  const { isLoading } = useHomeRole();
  const homeUrl = useHomeUrl();

  useEffect(() => {
    const isRootPath = location.pathname === "/";
    const isHomePath = location.pathname.includes("/home");
    const queryInviteLink = new URLSearchParams(window.location.search).get("invite_key");

    // Redirect if on root path or home path (without invite link), and have user data
    if (
      (isRootPath || isHomePath) &&
      !queryInviteLink &&
      userId &&
      !isLoading &&
      location.pathname !== homeUrl
    ) {
      // Defer navigation to next tick and mark as non-urgent to avoid race conditions
      // with DOM manipulation from IntercomProvider or other providers during navigation.
      // This is especially important on slower networks where components may still be
      // rendering when navigation occurs, causing "Failed to execute 'insertBefore' on 'Node'" errors.
      const timeoutId = setTimeout(() => {
        startTransition(() => {
          navigate(homeUrl);
        });
      }, 0);

      return () => {
        clearTimeout(timeoutId);
      };
    }
    // eslint-disable-next-line no-object-deps/no-object-deps
  }, [location.pathname, userId, isLoading, navigate, homeUrl]);

  useEffect(() => {
    const orgId = getOrgIdFromCookie();
    if (orgId) {
      trackUserProperty("orgId", orgId);
    }
  }, []);

  const orgRole = useAppSelector(selectCurrentOrgRole);
  useEffect(() => {
    if (orgRole) {
      trackUserProperty("orgRole", orgRole);
    }
  }, [orgRole]);

  // Base, tool_hint, section/page/outcomes context are built server-side via ContextBuilder.
  // This will be overwritten if we show a worksheet downstream
  useAIContext("worksheet", t("ai.no_content"), "assistant");

  useEffect(() => {
    // We want to make sure our Amplitude analytics are initialized with the user's email. We also
    // wait to get the user's MP admin state and course authoring status for session replay purposes.
    // We want to fully capture instructor's session replays, but we only
    // sample a small portion of student's session replays (and avoid capturing MP Admins)
    // given our limited session replay quota.
    // There are edge cases where this may not capture an instructor correctly (e.g. if they are
    // a student in the course they start in, then switch to another course as an instructor), but
    // this is a rare enough case that we don't need to worry about it.
    // Note this will stop analytics tracking if the email is set to null or undefined.
    if (isMPAdmin !== undefined && canAuthorSection !== undefined) {
      if (isMPAdmin) {
        // For MP Admins, we intentionally never initialize session replay to avoid
        // using up our session replay quota.
        maybeInitAmplitude(email!, 0.0);
      } else if (canAuthorSection) {
        maybeInitAmplitude(
          email!,
          window._env_.AMPLITUDE_SESSION_REPLAY_INSTRUCTOR_CAPTURE_RATE || 0,
        );
      } else {
        maybeInitAmplitude(email!, window._env_.AMPLITUDE_SESSION_REPLAY_STUDENT_CAPTURE_RATE || 0);
      }
    }
  }, [isMPAdmin, canAuthorSection, email]);

  // Refresh class session data when window regains focus
  // TODO: Store: If we have versioning in websocket messages and a replay feature,
  // we should replay missed messages instead.
  useEffect(() => {
    if (!classSession?.id) {
      return;
    }

    const fetchClassSessionData = () => {
      // Using setTimeout to delay in case coming back from inactive state
      setTimeout(() => {
        storeApi.class_sessions
          .retrieve(classSession?.id, { skipToast: true, skipCache: true })
          .catch((err) => {
            // We are piggybacking on this class session update to check for 401 errors
            // If we have 401 errors, we should log out the user.
            // Not all exceptions will have a response but this code should work fine if not
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (err.response?.status === 401) {
              // TODO: We don't clear out all the user data here like we do in the other serverLogout call
              //  and probably should
              serverLogout()
                .catch((e) => {
                  rollbarAndLogError("logout after 401 failed", e);
                })
                .finally(() => {
                  // Always clear user context
                  dispatch(authStateActions.setCurrentUser(null));
                });
            } else {
              toastLocalizedOperationError("fetch_class_session_failure");
              rollbarAndLogError("Class Session response status ", err);
            }
          });
      });
    };

    window.addEventListener("focus", fetchClassSessionData);
    return () => {
      window.removeEventListener("focus", fetchClassSessionData);
    };
    // eslint-disable-next-line no-object-deps/no-object-deps
  }, [classSession?.id, dispatch]);

  useEffect(() => {
    // Make sure that we always have the presented page loaded if a presentation is ongoing
    if (classSession?.presented_page_id) {
      storeApi.pages.retrieve(classSession.presented_page_id, { skipToast: true });
    }
  }, [classSession?.presented_page_id]);

  useEffect(() => {
    // This is only for the instructorView that is projecting not the projector view.
    if (!sectionId || isProjectorPath) {
      return;
    }

    // Instructor could have 2 tabs open for the same course aside from the projector view.  We only
    // want to listen for this event if we are the tab with focus.
    const confirmProjectorIfProjecting = () => {
      if (showingProjectorView) {
        confirmProjectorViewOpen(sectionId);
      }
    };

    // If this page gets refreshed while presenting we will lose the connection
    // to the projector view.  Here we recreate the connection if that happens.
    if (showingProjectorView) {
      confirmProjectorViewOpen(sectionId);

      window.addEventListener("focus", confirmProjectorIfProjecting);
    }

    return () => {
      window.removeEventListener("focus", confirmProjectorIfProjecting);
    };
  }, [sectionId, showingProjectorView, isProjectorPath]);

  // When a classSession changes from the backend
  useOnChanged(classSession, async (newSession, oldSession) => {
    if (!sectionId) {
      return;
    }
    const selectedPageChanged =
      newSession?.presented_page_id !== oldSession?.presented_page_id ||
      newSession?.is_practice ||
      !newSession?.presented_page_id;
    const userNoLongerPresenting =
      newSession?.presenting_user_id !== oldSession?.presenting_user_id &&
      oldSession?.presenting_user_id === userId;

    // Handle page transitions for users following a presentation, or the end of a presentation.
    // We need to check both isFollowing and hasFollowingParam here to cover edge cases. For a regular
    // user we can't rely on isFollowing because that becomes false at the moment the presentation ends,
    // before this callback runs, so to cover that case we have to check hasFollowingParam. For the
    // recording agent on the other hand, we can't rely on hasFollowingParam because it's always false,
    // and so to cover that case we have to check isFollowing.
    if (((isFollowing || hasFollowingParam) && selectedPageChanged) || userNoLongerPresenting) {
      if (newSession?.presented_page_id && !newSession.is_practice) {
        const state = store.getState();
        let page = pageSelectors.selectById(state, newSession.presented_page_id);
        if (!page) {
          page = await storeApi.pages.retrieve(newSession.presented_page_id, { skipToast: true });
        }

        const additionalParams = { [FOLLOWING_QUERY_PARAM]: "1" };
        const overlay = getQueryParam(SECONDARY_OVERLAY_QUERY_PARAM) as SecondaryOverlayType;
        if (shouldAddOverlayParam(overlay, isSmallScreen)) {
          additionalParams[SECONDARY_OVERLAY_QUERY_PARAM] = overlay;
        }

        const path = pageUrl(sectionId, newSession.presented_page_id, {
          // We have to pass this as we can't rely on the defaulting behavior of the sidebar,
          // since we want to specifically override any user-selected sidebar panel.
          // TODO: This could also potentially be confusing if users get their sidebar changed unexpectedly,
          // during a presentation, but syncing the instructor's sidebar tab state would be difficult too.
          [TAB_QUERY_PARAM]:
            SectionPageCategoryToSidebarTab[page.category] || CONTENT_SIDEBAR_TAB_NAME.Content,
          additionalParams,
        });
        // Defer navigation to next tick and mark as non-urgent to avoid race conditions
        // with DOM manipulation from IntercomProvider or other providers during navigation.
        // This is especially important on slower networks where components may still be
        // rendering when navigation occurs, causing "Failed to execute 'insertBefore' on 'Node'" errors.
        navigate(path);
      } else if (!newSession?.screensharing_class_user_id) {
        // We don't need to navigate to the first item just because we stopped presenting
        deleteQueryParams([FOLLOWING_QUERY_PARAM]);
      }
    }

    const screensharingEnded =
      oldSession?.screensharing_class_user_id && !newSession?.screensharing_class_user_id;
    const isPresentingUser = newSession?.presenting_user_id === userId;
    if (screensharingEnded && isPresentingUser) {
      deleteQueryParams([FOLLOWING_QUERY_PARAM]);
    }

    // When a class session ends entirely (instructor ends for everyone), remove following param.
    const sessionEnded = oldSession && !newSession;
    if (sessionEnded && hasFollowingParam) {
      deleteQueryParams([FOLLOWING_QUERY_PARAM]);
    }
    // Also remove the chat overlay param so the chat sidebar closes for everyone.
    const hadChatOverlayParam =
      getQueryParam(SECONDARY_OVERLAY_QUERY_PARAM) === SecondaryOverlay.ClassChat;
    if (sessionEnded && hadChatOverlayParam) {
      deleteQueryParams([SECONDARY_OVERLAY_QUERY_PARAM]);
    }

    const virtualClassEnded =
      oldSession?.category === ClassSessionCategory.VIRTUAL &&
      newSession?.category !== ClassSessionCategory.VIRTUAL;
    const presentationEnded = oldSession?.presented_page_id && !newSession?.presented_page_id;
    // If the virtual class ended or the presentation ended, and the other is not ongoing,
    // close the class chat if it is open.
    const shouldCloseClassChat =
      (virtualClassEnded && !newSession?.presented_page_id) ||
      (presentationEnded && newSession?.category !== ClassSessionCategory.VIRTUAL);
    if (shouldCloseClassChat) {
      // If the class chat is open, close it
      const overlay = getQueryParam(SECONDARY_OVERLAY_QUERY_PARAM);
      if (overlay === SecondaryOverlay.ClassChat) {
        deleteQueryParams([SECONDARY_OVERLAY_QUERY_PARAM]);
      }
    }
  });

  // If the selected page changes, remove `assessing` from query params
  // TODO: Warn users that they will lose their progress if they switch pages during assessment mode.
  // TODO: Or do we want to permanently disallow opening the sidebar during assessment mode?
  useOnChanged(selectedPageId, (prev, newPage) => {
    if (prev && newPage && prev !== newPage && !isSectionVersionOutOfSync) {
      deleteQueryParams([ASSESSMENT_VIEW_QUERY_PARAM]);
    }
  });

  // We don't want to set isAssessing to false if we are playing back previous versions of the course
  // until the playback completes and we're sure the page has changed.
  useEffect(() => {
    if (isSectionVersionOutOfSync && !preVersionPlaybackPageId.current) {
      preVersionPlaybackPageId.current = selectedPageId;
    } else if (!isSectionVersionOutOfSync && preVersionPlaybackPageId.current) {
      if (preVersionPlaybackPageId.current !== selectedPageId) {
        deleteQueryParams([ASSESSMENT_VIEW_QUERY_PARAM]);
      }

      preVersionPlaybackPageId.current = null;
    }
  }, [isSectionVersionOutOfSync, selectedPageId, deleteQueryParams]);

  const createMonitoringEvent = useCreateMonitoringEvent();

  // We want to ensure we don't accidentally leave edit mode enabled when we make UI
  // changes that hide the edit mode button. Components should usually check edit mode
  // + canAuthorOrg/canAuthorSection separately, but in case they don't, this helps to
  // ensure we don't have accidental edge cases.
  useEffect(() => {
    if (viewLevel.home && !canAuthorOrg) {
      dispatch(localStateActions.setIsEditMode(false));
    } else if ((viewLevel.section || viewLevel.page) && !canAuthorSection) {
      dispatch(localStateActions.setIsEditMode(false));
    }
  }, [viewLevel, canAuthorOrg, canAuthorSection, dispatch]);

  useEffect(() => {
    if (!sectionId) return;

    const createEnterClassMonitoringEvent = () => {
      const navigationTimingMetrics = getNavigationTimingMetrics();
      const { userAgent } = window.navigator;
      createMonitoringEvent(MonitoringEventTypeType.ENTER_CLASS, {
        userAgent,
        navigationTimingMetrics,
      });
    };

    // Wait for page to fully load before logging navigation timing
    if (document.readyState === "complete") {
      createEnterClassMonitoringEvent();
    } else {
      const handleLoad = () => {
        createEnterClassMonitoringEvent();
        window.removeEventListener("load", handleLoad);
      };

      window.addEventListener("load", handleLoad);

      return () => window.removeEventListener("load", handleLoad);
    }
  }, [createMonitoringEvent, sectionId]);

  // Check browser compatibility
  useEffect(() => {
    const isBrowserTooOld = () => {
      // Check for features that became widely available around 2-3 years ago
      // TODO: Update this check in 2026
      // app.asana.com/1/914015496185/project/1206079475640061/task/1211064808228985?focus=true
      const modernFeatures = [
        // ES2022 features
        typeof Array.prototype.at === "function",
        typeof Object.hasOwn === "function",
        // Modern web APIs from 2022
        "structuredClone" in window,
      ];

      return modernFeatures.some((feature) => !feature);
    };

    if (isBrowserTooOld()) {
      toastWarningMessage(t("warning.browser_too_old"));
      createMonitoringEvent(MonitoringEventTypeType.BROWSER_TOO_OLD, {});
    }
  }, [createMonitoringEvent]);

  const apiError = useAppSelector(selectApiError);
  useEffect(() => {
    if (apiError) {
      createMonitoringEvent(MonitoringEventTypeType.FAILED_API_REQUEST, {
        model: apiError.model,
        action: apiError.action,
        errorMessage: apiError.errorMessage,
      });
    }
    dispatch(localStateActions.setApiError(null));
  }, [createMonitoringEvent, apiError, dispatch]);

  // ---- Audio observers ----

  const classUsersInVirtualClass = useListSelector(selectClassUsersWithIsVisibleInClassCapability, [
    "hand_raised_at",
  ]);
  const previousHandRaisedUserIds = useRef<string[] | null>(null);
  const previousBreakoutEndsAt = useRef<string | null | undefined>(null);
  const isAudioInitialized = useRef(false);

  // Breakout: play ticking sound when approaching breakout end
  useBreakoutEndingSoonObserver({
    breakoutSessionEndsAt: classSession?.breakout_session_ends_at,
    isInVirtualClass,
  });

  // Set initial values
  useEffect(() => {
    if (isAudioInitialized.current) return;

    previousHandRaisedUserIds.current = classUsersInVirtualClass
      ?.filter((classUser) => classUser.hand_raised_at)
      .map((classUser) => classUser.id);

    previousBreakoutEndsAt.current = classSession?.breakout_session_ends_at;

    isAudioInitialized.current = !!(classSession && classUsersInVirtualClass !== null);
  }, [classSession, classUsersInVirtualClass]);

  // Hand raise: play sound for instructors when a user raises their hand
  useEffect(() => {
    const shouldPlaySound = isInVirtualClass && canAuthorSection;
    if (!shouldPlaySound) return;

    const currentHandRaisedUserIds = classUsersInVirtualClass
      ?.filter((classUser) => classUser.hand_raised_at)
      .map((classUser) => classUser.id);

    for (const classUserId of currentHandRaisedUserIds) {
      if (!previousHandRaisedUserIds.current?.includes(classUserId)) {
        playHandRaiseAudio();
      }
    }

    previousHandRaisedUserIds.current = currentHandRaisedUserIds;
    // Take care to not enable sound unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classUsersInVirtualClass]);

  // Breakout: play audio when a breakout session starts or ends
  useEffect(() => {
    const currentBreakoutEndsAt = classSession?.breakout_session_ends_at;

    const breakoutIsStarting =
      previousBreakoutEndsAt.current === null && classSession && currentBreakoutEndsAt !== null;
    const breakoutIsEnding =
      previousBreakoutEndsAt.current !== null && classSession && currentBreakoutEndsAt === null;

    if (isInVirtualClass && (breakoutIsStarting || breakoutIsEnding)) {
      stopTickingAudio();
      playBreakoutAudio();
    }

    previousBreakoutEndsAt.current = currentBreakoutEndsAt;
    // Take care to not enable sound unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classSession?.breakout_session_ends_at]);

  // Manage websocket reconnections for main AV tab

  const isRecordingAgent = useAppSelector(selectIsRecordingAgent);
  const isMainAvTab = useAppSelector(selectIsMainAvTab);
  const mainAvTabState = useAppSelector(selectAvTabState);
  const { sendPubsubVirtualClassStateMessage, sendObservingJoinMessage } = useAvPubsubMessaging();
  const isClassSessionVirtual = useAppSelector(selectIsClassSessionVirtual);
  const classUser = useAppSelector(selectCurrentClassUser);
  // Note: Don't use isInVirtualClass from useAv() here - it depends on isMainAvTab,
  // creating a circular dependency since this code sets the main AV tab state
  const isUserInVirtualClass =
    !isProjectorPath &&
    isClassSessionVirtual &&
    (classUser?.is_in_class_session || classUser?.is_performing_av_check);
  // Virtual class setup: instructor is performing AV check
  const isVirtualClassSetup = useMemo(
    () => canAuthorSection && classUser?.is_performing_av_check,
    [canAuthorSection, classUser?.is_performing_av_check],
  );
  const isVirtualSessionOrSetup = isAVEnabled() && (isUserInVirtualClass || isVirtualClassSetup);
  const { pubsubStatus } = useContext(PubsubWebsocketContext);

  useEffect(() => {
    // Calculate the desired AV tab state based on pubsub connection (AV uses pubsub, not sharedb)
    let desiredState: AvTabState | undefined = mainAvTabState;
    const reason: string[] = [];
    // Not a virtual session or setup: always NotMain
    if (!isVirtualSessionOrSetup) {
      reason.push("!isVirtualSessionOrSetup");
      desiredState = AvTabState.NotMain;
    }
    // This tab is the main AV tab
    else if (isMainAvTab) {
      reason.push("isMainAvTab");
      desiredState = AvTabState.Main;
    }
    // We were disconnected while this was the main AV tab
    else if (mainAvTabState === AvTabState.Main && pubsubStatus === "disconnected") {
      reason.push("disconnected-while-main");
      desiredState = AvTabState.Retrying;
    }
    // The main Av tab is now a different tab
    else if (mainAvTabState === AvTabState.Main && pubsubStatus === "connected" && !isMainAvTab) {
      reason.push("main-tab-moved-to-other-tab");
      desiredState = AvTabState.NotMain;
    }

    // Only dispatch if the desired state is different
    if (desiredState !== undefined && desiredState !== mainAvTabState) {
      logInfoWithDebugFlag("debug_virtual_class", "[Observers] Updating AV Tab State", {
        desiredState,
        mainAvTabState,
        reason,
      });
      dispatch(PubsubVirtualClassStateActions.updateAvTabState(desiredState));
    }

    // The pubsub websocket is connected again and we can try to join the virtual class
    if (mainAvTabState === AvTabState.Retrying && pubsubStatus === "connected") {
      // Send join message to pubsub
      logInfoWithDebugFlag(
        "debug_virtual_class",
        "[Observers] Pubsub websocket reconnected, sending virtual class join message",
      );
      sendPubsubVirtualClassStateMessage({
        action: "join",
        force: isRecordingAgent || false,
      });
    }
    logInfoWithDebugFlag("debug_virtual_class", "[Observers] AV Tab State update", {
      isMainAvTab,
      isRecordingAgent,
      isVirtualSessionOrSetup,
      mainAvTabState,
      desiredState,
      pubsubStatus,
    });
  }, [
    isMainAvTab,
    isRecordingAgent,
    isVirtualSessionOrSetup,
    mainAvTabState,
    pubsubStatus,
    sendPubsubVirtualClassStateMessage,
    dispatch,
  ]);

  const hasClassStateUpdate = !!useAppSelector(selectClassStateLastUpdatedAt);

  const isObservingClass = useAppSelector(selectIsObservingVirtualClass);
  // Send join as observer when user is observing and there is no class state
  useEffect(() => {
    if (isObservingClass && !hasClassStateUpdate) {
      sendObservingJoinMessage();
    }
  }, [isObservingClass, hasClassStateUpdate, sendObservingJoinMessage]);

  useParticipantBarObserver();

  return null;
};
